'use client'

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useMemo } from 'react'

import { getGatewayErrorCode } from '@/shared/api/client'

import {
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
  type NotificationListResponse,
  type UnreadCountResponse
} from '../api/notificationsApi'
import {
  clearFeed,
  type FeedData,
  feedItems,
  forgetOwnReads,
  invalidateFeed,
  recordOwnReads,
  removeFromFeed
} from '../lib/feedCache'
import {
  NOTIFICATIONS_PAGE_SIZE,
  notificationsQueryKeys
} from '../notifications.constants'
import { useNotificationsUserId } from './useNotificationsUserId'

/** Where the next page starts: a cursor, or an offset for an older gateway. */
type FeedPageParam = { cursor?: string; offset?: number }

const nextPageParam = (
  lastPage: NotificationListResponse,
  pages: NotificationListResponse[]
): FeedPageParam | undefined => {
  if (lastPage.next_cursor !== undefined) {
    return lastPage.next_cursor ? { cursor: lastPage.next_cursor } : undefined
  }

  // A gateway without `next_cursor` pages by offset only.
  const loaded = pages.reduce(
    (sum, page) => sum + (page.notifications?.length ?? 0),
    0
  )
  const lastSize = lastPage.notifications?.length ?? 0
  return lastSize > 0 && loaded < (lastPage.total ?? 0)
    ? { offset: loaded }
    : undefined
}

export function useNotificationsFeed(enabled = true) {
  const userId = useNotificationsUserId()

  const query = useInfiniteQuery({
    queryKey: notificationsQueryKeys.list(userId ?? ''),
    enabled: enabled && Boolean(userId),
    initialPageParam: {} as FeedPageParam,
    queryFn: ({ pageParam, signal }) =>
      getNotifications(
        { limit: NOTIFICATIONS_PAGE_SIZE, ...pageParam },
        { signal }
      ),
    getNextPageParam: nextPageParam
  })

  const items: NotificationItem[] = useMemo(
    () => feedItems(query.data),
    [query.data]
  )

  // The query object is returned whole: spreading it would subscribe the
  // caller to every field of the query and re-render on each of them.
  return { query, items }
}

export function useUnreadCount(enabled = true) {
  const userId = useNotificationsUserId()

  return useQuery({
    queryKey: notificationsQueryKeys.unreadCount(userId ?? ''),
    enabled: enabled && Boolean(userId),
    queryFn: getUnreadCount,
    select: data => data.count ?? 0
  })
}

/** NT-100: the notification no longer exists — for the fan, it is read. */
const isGone = (error: unknown) =>
  getGatewayErrorCode(error) === 'NT-100' ||
  (error as { status?: unknown } | null)?.status === 404

interface ReadContext {
  userId: string
  previousList?: FeedData
  previousCount?: UnreadCountResponse
  removed: number
}

/**
 * Reading removes the row at once and lowers the badge; the server's answer
 * only confirms it. A failure puts both back.
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  const userId = useNotificationsUserId()

  return useMutation({
    mutationFn: async (id: string): Promise<'read' | 'gone'> => {
      try {
        await markNotificationRead(id)
        return 'read'
      } catch (error) {
        if (isGone(error)) {
          return 'gone'
        }
        throw error
      }
    },
    onMutate: async (id): Promise<ReadContext | undefined> => {
      // The service echoes the read to every stream of the account, this
      // tab's included; the echo must not refetch what is already right.
      recordOwnReads([id])

      if (!userId) {
        return undefined
      }

      const listKey = notificationsQueryKeys.list(userId)
      const countKey = notificationsQueryKeys.unreadCount(userId)

      await Promise.all([
        queryClient.cancelQueries({ queryKey: listKey }),
        queryClient.cancelQueries({ queryKey: countKey })
      ])

      const previousList = queryClient.getQueryData<FeedData>(listKey)
      const previousCount =
        queryClient.getQueryData<UnreadCountResponse>(countKey)
      const { data, removed } = removeFromFeed(previousList, [id])

      if (removed > 0) {
        queryClient.setQueryData(listKey, data)
        if (previousCount) {
          queryClient.setQueryData<UnreadCountResponse>(countKey, {
            count: Math.max(0, (previousCount.count ?? 0) - removed)
          })
        }
      }

      return { userId, previousList, previousCount, removed }
    },
    onError: (_error, id, context) => {
      forgetOwnReads([id])
      if (!context) {
        return
      }

      queryClient.setQueryData(
        notificationsQueryKeys.list(context.userId),
        context.previousList
      )
      queryClient.setQueryData(
        notificationsQueryKeys.unreadCount(context.userId),
        context.previousCount
      )
      invalidateFeed(queryClient, context.userId)
    },
    onSuccess: (outcome, id, context) => {
      if (!context) {
        return
      }

      // A refetch that started before the server stored the read may have
      // brought the row back.
      const listKey = notificationsQueryKeys.list(context.userId)
      const { data, removed } = removeFromFeed(
        queryClient.getQueryData<FeedData>(listKey),
        [id]
      )
      if (removed > 0) {
        queryClient.setQueryData(listKey, data)
      }

      // A row outside the loaded pages (a push opened from the system tray),
      // one the server no longer has, or one a racing refetch counted again:
      // the badge is read from the server, now that the read is stored.
      if (context.removed === 0 || outcome === 'gone' || removed > 0) {
        void queryClient.invalidateQueries({
          queryKey: notificationsQueryKeys.unreadCount(context.userId)
        })
      }
    }
  })
}

interface ClearContext {
  userId: string
  previousList?: FeedData
  previousCount?: UnreadCountResponse
}

/**
 * "Clear all" empties the centre and the badge at once; the server's answer
 * only confirms it. A failure puts both back.
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()
  const userId = useNotificationsUserId()

  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onMutate: async (): Promise<ClearContext | undefined> => {
      if (!userId) {
        return undefined
      }

      const listKey = notificationsQueryKeys.list(userId)
      const countKey = notificationsQueryKeys.unreadCount(userId)

      await Promise.all([
        queryClient.cancelQueries({ queryKey: listKey }),
        queryClient.cancelQueries({ queryKey: countKey })
      ])

      const previousList = queryClient.getQueryData<FeedData>(listKey)
      const previousCount =
        queryClient.getQueryData<UnreadCountResponse>(countKey)

      queryClient.setQueryData(listKey, clearFeed(previousList))
      if (previousCount) {
        queryClient.setQueryData<UnreadCountResponse>(countKey, { count: 0 })
      }

      return { userId, previousList, previousCount }
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return
      }

      queryClient.setQueryData(
        notificationsQueryKeys.list(context.userId),
        context.previousList
      )
      queryClient.setQueryData(
        notificationsQueryKeys.unreadCount(context.userId),
        context.previousCount
      )
      invalidateFeed(queryClient, context.userId)
    },
    // A notification may have arrived while the request was on its way: the
    // feed and the badge are read again from the server.
    onSuccess: (_data, _variables, context) => {
      if (context) {
        invalidateFeed(queryClient, context.userId)
      }
    }
  })
}
